// @ts-check
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Page, Row, Widget, TabbedWidget } from 'shared/containers/portal_layout';
import { reduxForm } from 'utils/reduxFormWrapper';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import HForm, {
  PhoneMaskField,
  TextAreaField,
  SingleCheckboxField,
  RadioGroupField,
} from 'shared/containers/portal_layout/HForm';
import api from 'utils/api';
import LoadingContent from 'shared/components/LoadingContent';
import SpinnerButton from 'shared/components/SpinnerButton';
import TabPane from 'shared/components/TabPane';
import Errors from 'shared/components/Errors';

class New extends Component {
  constructor() {
    super();
    this.state = {
      fetched: false,
      includeInsurance: true,
      faxNote: '',
      faxSent: false,
      faxError: false,
    };
    this.sendFax = this.sendFax.bind(this);
    this.updatePreview = this.updatePreview.bind(this);
  }

  componentDidMount() {
    const {
      route: { apiEndpoint },
      params: { referralId },
    } = this.props;
    return api.get(`${apiEndpoint}/referrals/${referralId}/faxes/new`).then(response => {
      this.setState({
        isSenderOrReceiver: response.data.isSenderOrReceiver,
        referringLocationFaxNumber: response.data.referringLocationFaxNumber,
        receivingLocationFaxNumber: response.data.receivingLocationFaxNumber,
        receivingOrganizationFaxNumber: response.data.receivingOrganizationFaxNumber,
        fetched: true,
        handlesAllDocuments: response.data.handlesAllDocuments,
      });
    });
  }

  sendFax(formValues) {
    this.setState({ faxSent: false });
    this.setState({ faxError: false });
    const {
      route: { apiEndpoint },
      params: { referralId },
    } = this.props;
    return api
      .post(`${apiEndpoint}/referrals/${referralId}/faxes`, {
        fax_kind: formValues.faxKind,
        fax_number: formValues.faxNumber,
        fax_note: formValues.faxNote,
        include_insurance: formValues.includeInsurance,
      })
      .then(response => {
        this.setState({ faxSent: true });
        return FlashMessageActions.flashSuccess({ message: 'Fax Sent' });
      });
  }

  updatePreview(formValues) {
    this.setState({
      includeInsurance: formValues.includeInsurance,
      faxNote: formValues.faxNote || '',
    });
  }

  render() {
    const {
      route: { apiEndpoint },
      params: { referralId },
    } = this.props;
    const {
      isSenderOrReceiver,
      receivingLocationFaxNumber,
      receivingOrganizationFaxNumber,
      referringLocationFaxNumber,
    } = this.state;
    const initialFaxNumber =
      isSenderOrReceiver === 'sender'
        ? receivingLocationFaxNumber || receivingOrganizationFaxNumber
        : referringLocationFaxNumber;
    const initialFaxKind = isSenderOrReceiver === 'sender' ? 'referral' : 'referrer_update';
    return (
      <Page>
        <Row>
          {this.state.faxSent && (
            <div className="alert alert-success">You have successfully queued the fax!</div>
          )}
        </Row>
        <Row>
          <Widget title="Send Fax" span={12} className="nopadding">
            {this.state.fetched ? (
              <FormifiedFaxForm
                form="sendFax"
                initialValues={{
                  faxKind: initialFaxKind,
                  faxNumber: initialFaxNumber,
                  includeInsurance: true,
                }}
                sendFax={this.sendFax}
                updatePreview={this.updatePreview}
                showWarning={!this.state.handlesAllDocuments}
              />
            ) : (
              <LoadingContent />
            )}
          </Widget>
        </Row>

        <Row>
          <TabbedWidget span={12} className="nopadding">
            <TabPane tabName="Info Sheet">
              <InfoSheet
                apiEndpoint={apiEndpoint}
                referralId={referralId}
                faxNote={this.state.faxNote}
                includeInsurance={this.state.includeInsurance}
              />
            </TabPane>
            <TabPane tabName="Custom Doc">
              <CustomDoc apiEndpoint={apiEndpoint} referralId={referralId} />
            </TabPane>
            <TabPane tabName="Attachments">
              <ConcatenatedFileAttachments apiEndpoint={apiEndpoint} referralId={referralId} />
            </TabPane>
          </TabbedWidget>
        </Row>
      </Page>
    );
  }
}

const InfoSheet = ({ apiEndpoint, referralId, faxNote, includeInsurance }) => {
  const url = `${apiEndpoint}/referrals/${referralId}/info_sheet.pdf?fax_note=${faxNote}${
    includeInsurance ? '&include_insurance=true' : ''
  }`;
  return <iframe title="InfoSheet" height="500px" width="100%" src={url} />;
};

const CustomDoc = ({ apiEndpoint, referralId }) => (
  <iframe
    title="CustomDoc"
    height="500px"
    width="100%"
    src={`${apiEndpoint}/referrals/${referralId}/custom_doc.pdf`}
  />
);

class ConcatenatedFileAttachments extends Component {
  constructor() {
    super();
    this.state = { message: null, url: null };
  }

  componentDidMount() {
    const { apiEndpoint, referralId } = this.props;
    return api.get(`${apiEndpoint}/referrals/${referralId}/combined_documents`).then(response => {
      this.setState({ message: response.data.message, url: response.data.url });
    });
  }

  render() {
    if (this.state.message) return <p>{this.state.message}</p>;
    if (this.state.url)
      return (
        <iframe
          title="ConcatenatedFileAttachments"
          height="500px"
          width="100%"
          src={this.state.url}
        />
      );
    return <LoadingContent />;
  }
}

const FaxForm = props => (
  <HForm onSubmit={props.handleSubmit(props.sendFax)}>
    <Errors errors={props.error || []} />
    <RadioGroupField
      name="faxKind"
      label="Pick a type of fax to send:"
      options={[
        { value: 'referral', label: 'Entire Referral (includes all documents)' },
        { value: 'referrer_update', label: 'Referral Update (only includes info/status sheet)' },
      ]}
    />
    <PhoneMaskField name="faxNumber" label="Fax Number" style={{ width: 110 }} />
    <TextAreaField name="faxNote" label="Fax Note" className="input-xlarge" />
    <SingleCheckboxField name="includeInsurance" label="Include Insurance Info" />
    <div className="form-actions">
      {props.showWarning && (
        <div className="alert alert-error">
          Warning: 1 or more of the file attachments cannot be converted to PDF
        </div>
      )}
      <SpinnerButton
        type="button"
        onClick={props.handleSubmit(props.updatePreview)}
        className="btn btn-danger"
        loading={props.submitting}
      >
        Update Preview
      </SpinnerButton>{' '}
      <SpinnerButton type="submit" className="btn btn-primary" loading={props.submitting}>
        Send Fax
      </SpinnerButton>
    </div>
  </HForm>
);

FaxForm.propTypes = {
  showWarning: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
};

const FormifiedFaxForm = reduxForm({})(FaxForm);

export default New;
